/**
 * Fallback implementations of @dls/react-core components using MUI.
 * DLS-specific props (dls_variant, dls_size, etc.) are accepted and mapped
 * to their MUI equivalents so existing JSX can remain unchanged.
 */

import React from 'react';
import {
  Button as MuiButton,
  ButtonProps as MuiButtonProps,
  CircularProgress as MuiCircularProgress,
  CircularProgressProps as MuiCircularProgressProps,
  Typography as MuiTypography,
  TypographyProps as MuiTypographyProps,
  Dialog as MuiDialog,
  DialogProps as MuiDialogProps,
  DialogContent as MuiDialogContent,
  DialogTitle as MuiDialogTitle,
  DialogActions as MuiDialogActions,
  AppBar as MuiAppBar,
  AppBarProps as MuiAppBarProps,
  TextField as MuiTextField,
  TextFieldProps as MuiTextFieldProps,
  Input as MuiInput,
  InputProps as MuiInputProps,
} from '@mui/material';
import { ThemeProvider } from 'styled-components';
import componentColor, { dls } from './colors';

// ─── Button ──────────────────────────────────────────────────────────────────

type DlsVariant = 'primary' | 'secondary' | 'quietDefault' | 'quietEmphasis';

type ButtonProps = Omit<MuiButtonProps, 'variant'> & {
  dls_variant?: DlsVariant;
};

const DLS_BTN_MAP: Record<DlsVariant, MuiButtonProps['variant']> = {
  primary: 'contained',
  secondary: 'outlined',
  quietDefault: 'text',
  quietEmphasis: 'text',
};

export const Button: React.FC<ButtonProps> = ({ dls_variant, ...rest }) => (
  <MuiButton variant={dls_variant ? DLS_BTN_MAP[dls_variant] : 'contained'} {...rest} />
);

// ─── CircularProgress ────────────────────────────────────────────────────────

type DlsSize = 'small' | 'medium' | 'large';

type CircularProgressProps = Omit<MuiCircularProgressProps, 'size' | 'variant'> & {
  dls_size?: DlsSize;
  variant?: string;
};

const DLS_SIZE_MAP: Record<DlsSize, number> = { small: 20, medium: 40, large: 60 };

export const CircularProgress: React.FC<CircularProgressProps> = ({
  dls_size,
  variant,
  ...rest
}) => {
  void variant;
  return <MuiCircularProgress size={dls_size ? DLS_SIZE_MAP[dls_size] : 40} {...rest} />;
};

// ─── Typography ──────────────────────────────────────────────────────────────

type TypographyProps = MuiTypographyProps & { weight?: string };

export const Typography: React.FC<TypographyProps> = ({ weight, ...rest }) => {
  void weight;
  return <MuiTypography {...rest} />;
};

// ─── Dialog components ───────────────────────────────────────────────────────

export type DialogProps = MuiDialogProps & {
  /**
   * Called when Enter is pressed while the dialog has focus but no button is
   * focused. Lets a dialog mirror its primary action on Enter (keyboard parity
   * with clicking the primary button). A focused button keeps its native
   * Enter/Space behaviour, so this never hijacks the secondary action.
   */
  onEnterKey?: () => void;
};

export const Dialog: React.FC<DialogProps> = ({ onClose, onEnterKey, onKeyDown, ...rest }) => (
  <MuiDialog
    onClose={(event, reason) => {
      if (reason === 'backdropClick') return;
      onClose?.(event, reason);
    }}
    onKeyDown={(event) => {
      onKeyDown?.(event);
      if (
        onEnterKey &&
        event.key === 'Enter' &&
        !event.defaultPrevented &&
        !(event.target as HTMLElement).closest('button')
      ) {
        event.preventDefault();
        // The wizard step behind the dialog listens for Enter on window. Keep the
        // key from reaching it so the dialog's action is the only one that fires.
        event.stopPropagation();
        onEnterKey();
      }
    }}
    {...rest}
  />
);

export {
  MuiDialogContent as DialogContent,
  MuiDialogTitle as DialogTitle,
  MuiDialogActions as DialogActions,
};

// ─── AppBar ──────────────────────────────────────────────────────────────────

export const AppBar: React.FC<MuiAppBarProps> = (props) => <MuiAppBar {...props} />;

// ─── NavigationTheme ─────────────────────────────────────────────────────────
// In DLS this wraps content with darker nav bar colours — passthrough here.

export const NavigationTheme: React.FC<{ children?: React.ReactNode }> = ({ children }) => (
  <>{children}</>
);

// ─── TextField ───────────────────────────────────────────────────────────────

export const TextField: React.FC<MuiTextFieldProps> = (props) => <MuiTextField {...props} />;

// ─── Input ───────────────────────────────────────────────────────────────────

export const Input: React.FC<MuiInputProps> = (props) => <MuiInput {...props} />;

// ─── Label ───────────────────────────────────────────────────────────────────

type LabelProps = React.HTMLAttributes<HTMLSpanElement> & {
  label_variant?: string;
  children?: React.ReactNode;
};

export const Label: React.FC<LabelProps> = ({ label_variant, ...rest }) => {
  void label_variant;
  return <span {...rest} />;
};

// ─── ThemeProvider + defaults (for test utilities) ───────────────────────────

export { ThemeProvider };
export const defaultTheme: Record<string, unknown> = {};
export const useTheme = (): { color: string; tone: string; accent: string } => ({
  color: 'group-blue',
  tone: 'ultra-light',
  accent: 'orange',
});

// ─── ApplicationThemeConfiguration (type alias used in styledHelpers) ────────

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type ApplicationThemeConfiguration = Record<string, any>;

// ─── AboutPage layout components ─────────────────────────────────────────────

export const AboutScreenHeaderContainer: React.FC<{ children?: React.ReactNode }> = ({
  children,
}) => (
  <div style={{ display: 'flex', alignItems: 'center', padding: '24px 16px 16px' }}>{children}</div>
);

export const ShieldLogoContainer: React.FC<{ children?: React.ReactNode }> = ({ children }) => (
  <div style={{ marginRight: '16px', display: 'flex' }}>{children}</div>
);

export const AboutTitle: React.FC<React.HTMLAttributes<HTMLHeadingElement>> = ({
  children,
  ...rest
}) => (
  <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 400 }} {...rest}>
    {children}
  </h2>
);

export const AboutScreenSubtitleContainer: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({
  children,
  ...rest
}) => (
  <div
    style={{
      padding: '8px 16px',
      borderBottom: `1px solid ${componentColor(dls.separator.background.content.normal)}`,
    }}
    {...rest}
  >
    {children}
  </div>
);

export const AboutScreenSubtitleTypography: React.FC<TypographyProps> = ({ weight, ...rest }) => {
  void weight;
  return <MuiTypography {...rest} />;
};

export const BrandAlignLeft: React.FC<{ children?: React.ReactNode }> = ({ children }) => (
  <div style={{ display: 'flex', alignItems: 'center' }}>{children}</div>
);

export const BrandIconsContainer: React.FC<{ children?: React.ReactNode }> = ({ children }) => (
  <div style={{ display: 'flex', flexWrap: 'wrap', padding: '8px 16px', gap: '8px' }}>
    {children}
  </div>
);

type BrandTextTypographyProps = TypographyProps & { left?: number };

export const BrandTextTypography: React.FC<BrandTextTypographyProps> = ({
  left,
  weight,
  style,
  ...rest
}) => {
  void weight;
  return (
    <MuiTypography
      style={{ marginLeft: left != null ? `${left}px` : undefined, ...style }}
      {...rest}
    />
  );
};

export const AboutTextTypography: React.FC<TypographyProps> = ({ weight, ...rest }) => {
  void weight;
  return <MuiTypography {...rest} />;
};

export const BrandSectionContainer: React.FC<{ children?: React.ReactNode }> = ({ children }) => (
  <div style={{ padding: '16px' }}>{children}</div>
);

export const BrandAddressContainer: React.FC<{ children?: React.ReactNode }> = ({ children }) => (
  <div style={{ display: 'flex', alignItems: 'flex-start', padding: '8px 16px', gap: '8px' }}>
    {children}
  </div>
);

export const AboutScreenContent: React.FC<{ children?: React.ReactNode }> = ({ children }) => (
  <div style={{ width: '100%', maxWidth: '600px', padding: '0 16px 80px' }}>{children}</div>
);
